export const SETTINGS_PATH = '/me/settings';

/**
 * The tabs, in order (TMMAPP-0119 item 23, Operator 2026-10-08: the stacked cards of TMMAPP-0103 /
 * 0112 became tabs). Subscription holds billing and the AI wallet; AI Automation holds the AI and
 * automation settings (TMMAPP-0161 item 2, Operator 2026-10-20); Profile ends with Start over and Delete
 * account.
 */
export const SETTINGS_TABS = [
  'profile',
  'subscription',
  'ai-automation',
  'appearance',
  'sharing',
  'notifications',
  'taxes',
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

/** A link to a tab (`?tab=`). */
export function settingsHref(tab: SettingsTab): string {
  return `${SETTINGS_PATH}?tab=${tab}`;
}

export type SettingsNotify = (message: string, severity?: 'success' | 'error') => void;

/**
 * Where an older link lands: the sections and tabs before TMMAPP-0119 (`?section=` or `?tab=`)
 * name the tab that holds them now, so bookmarks keep working.
 */
const LEGACY_TABS: Readonly<Record<string, SettingsTab>> = {
  billing: 'subscription',
  automation: 'ai-automation',
  account: 'profile',
  language: 'profile',
  currency: 'profile',
  preferences: 'appearance',
  theme: 'appearance',
  taxReport: 'taxes',
  tax: 'taxes',
};

/** The tab a `?tab=` (or an older `?section=`) value names, or `null` for none. */
export function parseSettingsTab(raw: string | null): SettingsTab | null {
  if (raw === null) return null;
  const known = SETTINGS_TABS.find((tab) => tab === raw);
  if (known !== undefined) return known;
  return Object.hasOwn(LEGACY_TABS, raw) ? (LEGACY_TABS[raw] ?? null) : null;
}

/** A tab's id and its panel's, for `aria-controls` / `aria-labelledby`. */
export const settingsTabId = (tab: SettingsTab) => `settings-tab-${tab}`;
export const settingsPanelId = (tab: SettingsTab) => `settings-panel-${tab}`;
